import React, {useCallback, useRef, useState} from 'react';
import AIMessageGenerator from '../aiMessageGenerator/AIMessageGenerator';
import type {
    AIMessageGeneratorAssistantFooterProps,
    AIMessageGeneratorEmptyStateProps,
    AIMessageGeneratorPromptAreaProps,
    AIMessageGeneratorUserMessageProps,
} from '../aiMessageGenerator/utils/types';
import AiAgentFab from './components/aiAgentFab/AiAgentFab';
import AiAgentEmptyState from './components/aiAgentEmptyState/AiAgentEmptyState';
import AiAgentSuggestions from './components/aiAgentSuggestions/AiAgentSuggestions';
import AiAgentUserMessage from './components/aiAgentUserMessage/AiAgentUserMessage';
import AiAgentPromptArea from './components/aiAgentPromptArea/AiAgentPromptArea';
import AiAgentProposalConfirm from './components/aiAgentProposalConfirm/AiAgentProposalConfirm';
import {useAiAgentPanel} from './hooks/useAiAgentPanel';

const noFilePicker = () => {};

function AiAgent() {
    const {
        t,
        agentShow,
        openAgent,
        handleReset,
        handleSubmit,
        pendingAsk,
        clearAsk,
        pendingProposal,
        turnXHR,
    } = useAiAgentPanel();
    const openFilePickerRef = useRef<() => void>(noFilePicker);
    const [promptHint, setPromptHint] = useState<string | null>(null);

    const clearPromptHint = useCallback(() => setPromptHint(null), []);

    // The hint describes the suggestion that filled the box, so it belongs to that draft only.
    const handleResetChat = useCallback(() => {
        clearPromptHint();
        handleReset();
    }, [clearPromptHint, handleReset]);

    const renderEmptyState = useCallback(({onSuggestionFill}: AIMessageGeneratorEmptyStateProps) => (
        <AiAgentEmptyState>
            <AiAgentSuggestions
                onSuggestionFill={onSuggestionFill}
                onOpenFilePicker={() => openFilePickerRef.current()}
                onPromptHintChange={setPromptHint}
            />
        </AiAgentEmptyState>
    ), []);

    const renderPromptArea = useCallback((props: AIMessageGeneratorPromptAreaProps) => (
        <AiAgentPromptArea
            {...props}
            pendingAsk={pendingAsk}
            onClearAsk={clearAsk}
            openFilePickerRef={openFilePickerRef}
            promptHint={promptHint}
            onClearPromptHint={clearPromptHint}
        />
    ), [pendingAsk, clearAsk, promptHint, clearPromptHint]);

    // The summary bubble already lists the details, so the action lives in it instead of
    // repeating them in a card of its own.
    const renderAssistantFooter = useCallback(({isLast, draftLoading, onSubmitPrompt}: AIMessageGeneratorAssistantFooterProps) => (
        isLast && pendingProposal ? (
            <AiAgentProposalConfirm
                proposal={pendingProposal}
                disabled={draftLoading}
                onSubmitPrompt={onSubmitPrompt}
            />
        ) : null
    ), [pendingProposal]);

    const renderUserMessage = useCallback(({message, index, onEditMessage, draftLoading}: AIMessageGeneratorUserMessageProps) => (
        <AiAgentUserMessage
            message={message}
            index={index}
            onEditMessage={onEditMessage}
            draftLoading={draftLoading}
        />
    ), []);

    return (
        <>
            <AiAgentFab onClick={openAgent} />
            <AIMessageGenerator
                show={agentShow}
                title={t('title')}
                placeholder={t('inputPlaceholder')}
                newChatLabel={t('newChat')}
                errorMessage={t('errorMessage')}
                suggestions={[]}
                submit={{label: t('send'), onClick: handleSubmit, xhrState: turnXHR}}
                renderEmptyState={renderEmptyState}
                renderPromptArea={renderPromptArea}
                renderUserMessage={renderUserMessage}
                renderAssistantFooter={renderAssistantFooter}
                onReset={handleResetChat}
                closeOnOutsideClick={false}
                hideBackdrop
                resetOnClose={false}
                dataCy="ai-agent"
            />
        </>
    );
}

export default AiAgent;
